import React, { useState, useMemo } from "react";
import Icon1 from "@assets/icons/Icon";

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfWeek(year: number, month: number) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_HEADERS = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];

/** Get Mon–Fri ISO dates for the week containing `dateStr` */
function getWeekDates(dateStr: string): Set<string> {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // offset to Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  const dates = new Set<string>();
  for (let i = 0; i < 5; i++) {
    const curr = new Date(monday);
    curr.setDate(monday.getDate() + i);
    dates.add(
      `${curr.getFullYear()}-${String(curr.getMonth() + 1).padStart(2, "0")}-${String(curr.getDate()).padStart(2, "0")}`,
    );
  }
  return dates;
}

function shiftDate(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface MiniCalendarProps {
  selectedDate: string;
  onSelectDate: (d: string) => void;
  /** When true, highlights the Mon–Fri week of the selected date */
  weekHighlight?: boolean;
  /** When true, shows prev/next day buttons below the calendar */
  showNavButtons?: boolean;
}

export default function MiniCalendar({ selectedDate, onSelectDate, weekHighlight = false, showNavButtons = false }: MiniCalendarProps) {
  const selected = new Date(selectedDate);
  const [viewYear, setViewYear] = useState(selected.getFullYear());
  const [viewMonth, setViewMonth] = useState(selected.getMonth());
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
  const prevMonthDays = getDaysInMonth(
    viewMonth === 0 ? viewYear - 1 : viewYear,
    viewMonth === 0 ? 11 : viewMonth - 1,
  );
  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };
  const toISO = (day: number) =>
    `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const isSelected = (day: number) => toISO(day) === selectedDate;

  const weekDates = useMemo(
    () => (weekHighlight ? getWeekDates(selectedDate) : new Set<string>()),
    [selectedDate, weekHighlight],
  );
  const isInWeek = (day: number) => weekHighlight && weekDates.has(toISO(day));

  return (
    <div className="flex flex-col" style={{ gap: 10 }}>
      <div className="flex flex-row items-center justify-between">
        <button onClick={prevMonth} className="flex items-center justify-center" style={{ width: 28, height: 28 }}>
          <Icon1 name="left" size={10} color="#A1A1A1" />
        </button>
        <span className="text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1" }}>
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button onClick={nextMonth} className="flex items-center justify-center" style={{ width: 28, height: 28 }}>
          <Icon1 name="right" size={10} color="#A1A1A1" />
        </button>
      </div>
      <div className="grid grid-cols-7">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="flex justify-center text-h4 font-medium" style={{ fontFamily: "Poppins", color: "#A1A1A1", height: 18 }}>
            {d}
          </div>
        ))}
      </div>
      <div className="flex flex-col" style={{ gap: 5 }}>
        {(() => {
          const rows: React.ReactElement[] = [];
          let cells: React.ReactElement[] = [];
          for (let i = 0; i < firstDay; i++)
            cells.push(
              <div key={`p${i}`} className="flex justify-center items-center text-h4 font-medium" style={{ height: 25, fontFamily: "Poppins", color: "#D9D9D9" }}>
                {prevMonthDays - firstDay + 1 + i}
              </div>,
            );
          for (let d = 1; d <= daysInMonth; d++) {
            const sel = isSelected(d);
            const inWeek = isInWeek(d) && !sel;
            cells.push(
              <button
                key={d}
                onClick={() => onSelectDate(toISO(d))}
                className="flex justify-center items-center text-h4 font-medium"
                style={{
                  height: 25,
                  width: 25,
                  margin: "0 auto",
                  fontFamily: "Poppins",
                  color: sel ? "#FFF" : inWeek ? "#5C6BFF" : "#A1A1A1",
                  background: sel ? "#000" : inWeek ? "#ECEEFF" : "transparent",
                  borderRadius: 30,
                  boxShadow: sel ? "0px 2px 4px rgba(0,0,0,0.15)" : "none",
                }}
              >
                {d}
              </button>,
            );
            if ((firstDay + d) % 7 === 0) {
              rows.push(<div key={`r${rows.length}`} className="grid grid-cols-7">{cells}</div>);
              cells = [];
            }
          }
          if (cells.length > 0) {
            let n = 1;
            while (cells.length < 7)
              cells.push(
                <div key={`n${n}`} className="flex justify-center items-center text-h4 font-medium" style={{ height: 25, fontFamily: "Poppins", color: "#D9D9D9" }}>
                  {n++}
                </div>,
              );
            rows.push(<div key={`r${rows.length}`} className="grid grid-cols-7">{cells}</div>);
          }
          return rows;
        })()}
      </div>
      {showNavButtons && (
        <div className="flex flex-row items-center justify-between" style={{ gap: 6 }}>
          <button
            onClick={() => onSelectDate(shiftDate(selectedDate, -1))}
            className="flex-1 flex flex-row items-center justify-center gap-1 rounded-lg hover:bg-neutral-100"
            style={{ height: 30, border: "1px solid #E5E5E5" }}
          >
            <Icon1 name="left" size={8} color="#A1A1A1" />
            <span className="text-h4" style={{ color: "#A1A1A1" }}>Préc.</span>
          </button>
          <button
            onClick={() => {
              const today = new Date();
              onSelectDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`);
            }}
            className="flex-1 flex items-center justify-center rounded-lg hover:bg-neutral-100"
            style={{ height: 30, border: "1px solid #E5E5E5" }}
          >
            <span className="text-h4 font-medium" style={{ color: "#5C6BFF" }}>Auj.</span>
          </button>
          <button
            onClick={() => onSelectDate(shiftDate(selectedDate, 1))}
            className="flex-1 flex flex-row items-center justify-center gap-1 rounded-lg hover:bg-neutral-100"
            style={{ height: 30, border: "1px solid #E5E5E5" }}
          >
            <span className="text-h4" style={{ color: "#A1A1A1" }}>Suiv.</span>
            <Icon1 name="right" size={8} color="#A1A1A1" />
          </button>
        </div>
      )}
    </div>
  );
}
